import { SxProps, TextField, useTheme, InputAdornment } from "@mui/material";
import { Theme } from "@mui/system";
import React from "react";

export type FormTextFieldVariants = "outlined" | "standard" | "filled";
export type FormTextFieldColorVariants = "primary" | "secondary";

export interface FormTextFieldProps {
  id?: string;
  label?: string;
  title?: string;
  fontSize?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  type?: string;
  color?: FormTextFieldColorVariants;
  variant?: FormTextFieldVariants;
  onChange?: (id?: string, value?: string) => void;
  value?: string | number;
  disabled?: boolean;
  backgroundColor?: string;
  errorMessage?: string;
  error?: boolean;
  placeholder?: string;
  sx?: SxProps<Theme>;
  focused?: boolean;
  inputRef?: any;
  borderColor?: string;
  maxLength?: number;
  marginBottom?: number;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

const FormSmallTextField = ({
  id,
  label,
  title,
  multiline,
  rows,
  minRows,
  maxRows,
  fontSize = "13px",
  required,
  color,
  type,
  variant = "outlined",
  onChange,
  value,
  disabled,
  backgroundColor,
  errorMessage = "",
  error = false,
  placeholder = "",
  sx,
  focused = false,
  inputRef = null,
  maxLength,
  borderColor = "#2A282E",
  marginBottom = 4,
  startAdornment,
  endAdornment,
  onKeyDown,
  onBlur,
  onFocus,
}: FormTextFieldProps) => {
  const theme = useTheme();
  return (
    <TextField
      autoComplete="off"
      id={id}
      inputRef={inputRef}
      focused={focused}
      disabled={disabled}
      label={title || (placeholder ? undefined : label)}
      required={required}
      multiline={multiline}
      rows={minRows != null || maxRows != null ? undefined : rows}
      minRows={minRows}
      maxRows={maxRows}
      variant={variant}
      color={color}
      type={type}
      size="small"
      error={error}
      placeholder={placeholder || undefined}
      inputProps={{ maxLength: maxLength }}
      InputProps={{
        startAdornment: startAdornment ? (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ) : undefined,
        endAdornment: endAdornment ? (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ) : undefined,
      }}
      helperText={error ? errorMessage : ""}
      onChange={(event) => {
        if (onChange) {
          const newValue = event.target.value;
          // Enforce maxLength if specified
          if (maxLength && newValue.length > maxLength) {
            // Truncate the value to maxLength
            const truncatedValue = newValue.slice(0, maxLength);
            onChange(id, truncatedValue);
            // Update the input value to show the truncated text
            event.target.value = truncatedValue;
          } else {
            onChange(id, newValue);
          }
        }
      }}
      value={value}
      slotProps={{
        inputLabel: { style: { fontSize: fontSize } },
        htmlInput: { style: { fontSize: fontSize } },
      }}
      onWheel={(e) => {
        if (type === "number") {
          e.preventDefault();
          (e.target as HTMLElement).blur();
        }
      }}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onFocus={onFocus}
      fullWidth
      sx={{
        mb: marginBottom,
        background: backgroundColor,
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: error ? "#AF095E" : disabled ? "#E0E0E0" : borderColor,
          },
          "&:hover fieldset": {
            borderColor: error ? "#AF095E" : "#6455ED",
          },
          "&.Mui-focused fieldset": {
            borderColor: error ? "#AF095E" : "#6455ED",
            borderWidth: "1px",
          },
          "&.Mui-disabled": {
            backgroundColor: theme.palette.customColors.greyBodyBackground,
          },
        },
        "& .MuiInputLabel-root": {
          color: disabled ? "#E0E0E0" : "#93969B",
          fontSize: fontSize,
          "&.Mui-focused": {
            color: error ? "#AF095E" : "#6455ED",
          },
        },
        "& .MuiInputBase-input": {
          color: disabled ? "#E0E0E0" : "#000",
          fontSize: fontSize,
        },
        "& .MuiInputAdornment-root": {
          "&.MuiInputAdornment-positionStart": {
            paddingLeft: "0px",
            marginLeft: "-7px",
            marginRight: "7px",
          },
        },
        "& .MuiFormHelperText-root": {
          color: "#AF095E",
          fontSize: "12px",
          marginTop: "4px",
        },
        ...sx,
      }}
    />
  );
};

export default React.memo(FormSmallTextField);
